import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useMotionPreferences } from '../../../context/MotionPreferencesContext'
import styles from './CustomCursor.module.css'

/**
 * CustomCursor Component
 *
 * A dramatic custom cursor with:
 * - Main cursor dot with glow
 * - Trailing particles that follow
 * - Expands on interactive elements
 * - Clearly visible on any background
 */

const TRAIL_LENGTH = 12

const CustomCursor = () => {
  const cursorRef = useRef(null)
  const trailRefs = useRef([])
  const mousePos = useRef({ x: -100, y: -100 })
  const trailPositions = useRef(Array(TRAIL_LENGTH).fill({ x: -100, y: -100 }))
  const [isHovering, setIsHovering] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const animationRef = useRef(null)
  const { reducedMotion } = useMotionPreferences()

  const animate = useCallback(() => {
    // Update main cursor
    if (cursorRef.current) {
      cursorRef.current.style.transform = `translate(${mousePos.current.x}px, ${mousePos.current.y}px)`
    }

    // Update trail with staggered following
    const newTrailPositions = [...trailPositions.current]
    for (let i = 0; i < TRAIL_LENGTH; i++) {
      const target = i === 0 ? mousePos.current : newTrailPositions[i - 1]
      const ease = 0.3 - (i * 0.015) // Decreasing ease for each trail dot

      newTrailPositions[i] = {
        x: newTrailPositions[i].x + (target.x - newTrailPositions[i].x) * ease,
        y: newTrailPositions[i].y + (target.y - newTrailPositions[i].y) * ease,
      }

      if (trailRefs.current[i]) {
        trailRefs.current[i].style.transform = `translate(${newTrailPositions[i].x}px, ${newTrailPositions[i].y}px)`
      }
    }
    trailPositions.current = newTrailPositions

    animationRef.current = requestAnimationFrame(animate)
  }, [])

  useEffect(() => {
    // Don't show custom cursor on touch devices or if reduced motion
    const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
    if (reducedMotion || isTouchDevice) {
      return
    }

    const handleMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY }
      if (!isVisible) setIsVisible(true)
    }

    const handleMouseLeave = () => setIsVisible(false)
    const handleMouseEnter = () => setIsVisible(true)

    // Check for interactive elements
    const handleElementHover = (e) => {
      const target = e.target
      const isInteractive =
        target.tagName === 'A' ||
        target.tagName === 'BUTTON' ||
        target.closest('a') ||
        target.closest('button') ||
        target.hasAttribute('data-cursor-hover') ||
        target.closest('[data-cursor-hover]')
      setIsHovering(isInteractive)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseleave', handleMouseLeave)
    document.addEventListener('mouseenter', handleMouseEnter)
    document.addEventListener('mouseover', handleElementHover)

    // Start animation
    animationRef.current = requestAnimationFrame(animate)

    // Hide default cursor globally
    const style = document.createElement('style')
    style.id = 'cursor-hide'
    style.textContent = '* { cursor: none !important; }'
    document.head.appendChild(style)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseleave', handleMouseLeave)
      document.removeEventListener('mouseenter', handleMouseEnter)
      document.removeEventListener('mouseover', handleElementHover)
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
      const styleEl = document.getElementById('cursor-hide')
      if (styleEl) styleEl.remove()
    }
  }, [reducedMotion, animate, isVisible])

  // Don't render on touch devices or reduced motion
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  if (reducedMotion || isTouchDevice) {
    return null
  }

  return (
    <div className={`${styles.cursorContainer} ${isVisible ? styles.visible : ''}`}>
      {/* Trail dots */}
      {Array.from({ length: TRAIL_LENGTH }).map((_, i) => (
        <div
          key={i}
          ref={(el) => (trailRefs.current[i] = el)}
          className={`${styles.trailDot} ${isHovering ? styles.hovering : ''}`}
          style={{
            '--index': i,
            '--opacity': 1 - (i / TRAIL_LENGTH),
            '--scale': 1 - (i / TRAIL_LENGTH) * 0.7,
          }}
        />
      ))}
      {/* Main cursor */}
      <div
        ref={cursorRef}
        className={`${styles.mainCursor} ${isHovering ? styles.hovering : ''}`}
      />
    </div>
  )
}

export default CustomCursor
